<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { Loader2, TriangleAlert } from 'lucide-vue-next'
import { configure, useForm } from 'vee-validate'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import * as z from 'zod'
import DialogCheck from '@/components/DialogCheck.vue'
import { useErrorMessage } from '@/composables'
import { Alert, AlertDescription } from '@/foundation/alert'
import { AutoForm } from '@/foundation/auto-form'
import { Button } from '@/foundation/button'
import { createQueue, updateQueue } from '@/service/tickets'
import { useQueuesStore } from '@/stores/queues'
import { useTeamStore } from '@/stores/teams'

configure({
  bails: false,
  validateOnBlur: false,
  validateOnChange: false,
  validateOnInput: false,
  validateOnModelUpdate: false,
})

const props = defineProps({
  defaultQueue: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  queueId: {
    type: String,
    default: null,
  },
  workflowOptions: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['closeDialog', 'updateSuccess', 'createSuccess'])

const queueStore = useQueuesStore()
const teamStore = useTeamStore()
const router = useRouter()

const isLoading = ref(false)
const isSubmitting = ref(false)
// Define Form Schema using zod
const schema = z.object({
  queue: z
    .string({
      required_error: 'Queue name is required.',
    })
    .nonempty('Queue name is required.'),
  description: z.string().optional().nullable(),
  assignee: z
    .any({
      required_error: 'Assignment option is required.',
    })
    .refine(
      (val) => {
        if (!val || Object.keys(val).length === 0) {
          return false
        }

        if (val.teams !== undefined) {
          return Array.isArray(val.teams) && val.teams.length > 0
        }

        if (val.users !== undefined) {
          return Array.isArray(val.users) && val.users.length > 0
        }

        if (val.all) {
          return true
        }

        return false
      },
      {
        message: 'Please select at least one team or user when choosing Teams or Users option.',
      },
    ),
})

// Field Configuration
const fieldConfig = reactive({
  queue: {
    label: 'Name',
    inputProps: {
      class: 'w-full lg:w-3/5',
      placeholder: 'Queue Name',
    },
  },
  description: {
    label: 'Description',
    component: 'textarea',
    inputProps: {
      class: 'w-full lg:w-3/5',
      placeholder: 'Description',
      rows: 3,
    },
  },
  assignee: {
    label: 'Assignee to',
    component: 'multibox',
    inputProps: {
      showIcon: true,
      placeholder: 'Select',
      insideText: '',
      class: 'w-full lg:w-3/5',
      options: [
        { key: 'all', label: 'Assigned to all' },
        { key: 'teams', label: 'Teams', options: teamStore.teams, type: 'multiselect' },
        { key: 'users', label: 'Users', options: teamStore.allAssigneeUsers, type: 'multiselect' },
      ],
    },
  },
})

const translateAssignee = (value: any) => {
  if (value?.toAll) return { all: true }
  if (Array.isArray(value?.teams) && value.teams.length > 0) {
    return {
      teams: value.teams,
    }
  }
  if (Array.isArray(value?.userIds) && value.userIds.length > 0) {
    return {
      users: teamStore.allAssigneeUsers.filter((user) => value.userIds.includes(user.id)),
    }
  }

  return value
}
const form = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues: props.defaultQueue
    ? {
        queue: props.defaultQueue.title,
        description: props.defaultQueue.description,
        assignee: translateAssignee(props.defaultQueue),
      }
    : {
        assignee: { all: true },
      },
})

// Handle Form Submission
const onSubmit = async (values: Record<string, any>) => {
  isLoading.value = true

  if (props.isEditing) {
    const { onSuccess, onError } = updateQueue(props.queueId, {
      title: values.queue,
      description: values.description,
      assignee: values.assignee,
    })

    onSuccess((data) => {
      queueStore.fetchQueues()

      const prevTeamIds = props.defaultQueue?.teams?.map((t: any) => t.id) || []
      const newTeamIds = values.assignee?.teams?.map((t: any) => t.id) || []

      teamStore.updateQueueAffiliationInTeams(props.queueId, values.queue, props.defaultQueue?.slug, prevTeamIds, newTeamIds)

      emit('closeDialog')
      emit('updateSuccess')
      isLoading.value = false
    })
    onError((data) => {
      if (data.status === 409) {
        form.setErrors({
          queue: 'Queue name already exists.',
        })
      }
      isLoading.value = false
    })
  } else {
    const { onSuccess, onError } = createQueue({
      title: values.queue,
      description: values.description,
      assignee: values.assignee,
    })

    onSuccess(async (data) => {
      await queueStore.fetchQueues()

      const newTeamIds = values.assignee?.teams?.map((t: any) => t.id) || []

      teamStore.updateQueueAffiliationInTeams(data.id, values.queue, data.slug, [], newTeamIds)

      router.push({
        name: 'QueueSettings',
        params: { id: data.id },
      })
      emit('closeDialog')
      emit('createSuccess')
      isLoading.value = false
    })
    onError((data) => {
      if (data.status === 409) {
        form.setErrors({
          queue: 'Queue name already exists.',
        })
      }
      isLoading.value = false
    })
  }
}

const { errorMessage } = useErrorMessage(isSubmitting, form.errors)
</script>

<template>
  <div class="flex flex-col w-full">
    <Alert v-if="errorMessage" variant="destructive" class="text-white !bg-destructive !flex items-center">
      <TriangleAlert color="white" class="w-4 h-4" />
      <AlertDescription class="font-semibold">
        {{ errorMessage }}
      </AlertDescription>
    </Alert>

    <div class="flex w-full">
      <AutoForm class="w-full space-y-3" :schema="schema" :field-config="fieldConfig" :form="form" @submit="onSubmit">
        <div class="h-6">
          <div
            class="flex justify-end w-full gap-2 absolute bottom-0 p-4 rounded-b-sm right-0 border-t-2 border-border z-[100] bg-create-form-footer"
          >
            <DialogCheck :show-icon="true" @close-dialog="$emit('closeDialog')" />
            <slot v-bind="{ onSubmit }">
              <Button type="submit" :disabled="isLoading" @click="isSubmitting = true">
                <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" /> {{ isEditing ? 'Save' : 'Create' }}
              </Button>
            </slot>
          </div>
        </div>
      </AutoForm>
    </div>
  </div>
</template>
